/**
 * L1/FLS Review Queue (Beta-1 plan P2-09 / P2-10 / P2-12 / P2-13 / P2-36; Tim's D06 §5, D03 §C.6).
 *
 * Items reach this queue by routing (conditionWorkflow.js): the L1 authority for an IC's
 * condition is the FLS of its NWT; for an FLS's own condition it is the DH; and so on (P2-37).
 * Opening an item flips it to Review in Progress-L1 so the entrant sees where it is (P2-09).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Chip, Stack, Divider, ToggleButtonGroup, ToggleButton, TextField, MenuItem, Alert, CircularProgress, Collapse, IconButton, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { SEVERITY_LEVELS, REF_ACTIONS, refActionLabel, CONDITION_STATUS } from '../../utils/trlConstants';
import { useTRLs } from '../../hooks/useTRLs';
import { useConditionViewer } from '../../hooks/useConditionViewer';
import { flsApproveTRL, flsRejectTRL, openL1, approveResolution, returnResolution, hideConditionFor, reviseDecision, cancelCondition } from '../../../../services/trlService';
import { needsL1Action, isL1For, isSingleStep, canApproveResolution, canRevise, canHide, canCancel, isHiddenForViewer } from '../../utils/conditionWorkflow';
import { ConditionHeader, SeverityChips } from '../../components/conditions/ConditionHeader';
import { NoteDialog, OwnerFields, InlineNotice } from '../../components/conditions/ConditionDialogs';
import { notifyConditionEvent } from '../../../../services/conditionNotificationService';

const FILTERS = [
  { key: 'queue', label: 'Needs my review' },
  { key: 'resolutions', label: 'Resolutions to approve' },
  { key: 'at_l2', label: 'Review in Progress-L2' },
  { key: 'approved', label: 'Approved/Active' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

function ReviewQueue() {
  const viewer = useConditionViewer();
  const { trls, loading } = useTRLs({ limit: 300 });
  const [filter, setFilter] = useState('queue');
  const [openId, setOpenId] = useState(null);
  const [forms, setForms] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null); // { kind: 'reject'|'return_resolution'|'cancel'|'revise', condition }
  const [searchParams, setSearchParams] = useSearchParams();

  // Bell / activity deep link: ?condition=<id> opens that item whatever its filter
  useEffect(() => {
    const id = searchParams.get('condition');
    if (!id || !trls.some((t) => t.id === id)) return;
    setFilter('all');
    setOpenId(id);
    setSearchParams({}, { replace: true });
  }, [searchParams, trls, setSearchParams]);

  const visible = useMemo(() => trls.filter((t) => !isHiddenForViewer(t, viewer)), [trls, viewer]);
  const counts = useMemo(
    () => ({
      queue: visible.filter((t) => needsL1Action(t, viewer, viewer.routing) && t.status !== CONDITION_STATUS.resolution_pending).length,
      resolutions: visible.filter((t) => t.status === CONDITION_STATUS.resolution_pending && canApproveResolution(t, viewer, viewer.routing) === 'l1').length,
      at_l2: visible.filter((t) => t.status === CONDITION_STATUS.under_review).length,
      approved: visible.filter((t) => t.status === CONDITION_STATUS.approved).length,
      closed: visible.filter((t) => ['resolved', 'rejected', 'withdrawn', 'cancelled'].includes(t.status)).length,
      all: visible.length,
    }),
    [visible, viewer]
  );
  const filtered = visible.filter((t) => {
    switch (filter) {
      case 'queue':
        return needsL1Action(t, viewer, viewer.routing) && t.status !== CONDITION_STATUS.resolution_pending;
      case 'resolutions':
        return t.status === CONDITION_STATUS.resolution_pending && canApproveResolution(t, viewer, viewer.routing) === 'l1';
      case 'at_l2':
        return t.status === CONDITION_STATUS.under_review;
      case 'approved':
        return t.status === CONDITION_STATUS.approved;
      case 'closed':
        return ['resolved', 'rejected', 'withdrawn', 'cancelled'].includes(t.status);
      default:
        return true;
    }
  });

  const formOf = (t) =>
    forms[t.id] || {
      sev: t.severityFLS || t.severityITM || 'II',
      ref: t.l1?.refAction || (t.refActions || []).find((a) => !a.level || a.level === 'l1')?.type || '',
      note: t.l1?.notes || '',
      owner: { ownerDeptId: t.ownerDeptId || '', ownerNwtId: t.ownerNwtId || '', ownerUid: t.ownerUid || '' },
    };
  const setForm = (id, patch) => setForms((f) => ({ ...f, [id]: { ...formOf({ id }), ...(f[id] || {}), ...patch } }));

  const actor = { by: viewer.uid, byName: viewer.name, actingFor: viewer.actingFor };

  const toggleOpen = async (t) => {
    const next = openId === t.id ? null : t.id;
    setOpenId(next);
    if (next && t.status === CONDITION_STATUS.submitted && isL1For(t, viewer, viewer.routing)) {
      try {
        const changed = await openL1(t.id, actor); // P2-09: Pending Review → Review in Progress-L1
        if (changed) notifyConditionEvent('l1_opened', t, viewer, { after: { status: 'review_l1' } });
      } catch (err) {
        console.warn('openL1:', err.message);
      }
    }
  };

  const run = async (id, fn, success) => {
    setBusyId(id);
    setNotice(null);
    try {
      await fn();
      if (success) setNotice({ severity: 'success', message: success });
      setDialog(null);
    } catch (err) {
      console.error(err);
      setNotice({ severity: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setBusyId(null);
    }
  };

  const approve = (t) => {
    const f = formOf(t);
    const single = isSingleStep(t, viewer.routing);
    return run(
      t.id,
      async () => {
        await flsApproveTRL(t.id, {
          severityFLS: f.sev,
          refAction: f.ref || null,
          notes: f.note,
          ownerDeptId: f.owner.ownerDeptId || null,
          ownerNwtId: f.owner.ownerNwtId || null,
          approvedBy: viewer.uid,
          approvedByName: viewer.name,
          actingFor: viewer.actingFor,
          singleStep: single,
        });
        notifyConditionEvent('l1_approved', t, viewer, { after: { ownerDeptId: f.owner.ownerDeptId || null, ownerNwtId: f.owner.ownerNwtId || null }, actedAs: 'reviewed an OE Condition' });
      },
      single ? 'Approved — the condition is now Approved/Active.' : 'Approved for L2/DH Review.'
    );
  };

  const amend = (t) => {
    const f = formOf(t);
    return run(
      t.id,
      () => flsApproveTRL(t.id, { severityFLS: f.sev, refAction: f.ref || null, notes: f.note, ownerDeptId: f.owner.ownerDeptId || null, ownerNwtId: f.owner.ownerNwtId || null, approvedBy: viewer.uid, approvedByName: viewer.name, actingFor: viewer.actingFor }),
      'Review updated.'
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <RuleFolderIcon sx={{ mr: 1, color: 'warning.main', fontSize: 32 }} />
        <Typography variant="h4">L1/FLS Review Queue</Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review OE Conditions submitted by your team/crew members, confirm/adjust indicated Severity Level, identify your Proposed REF Action(s) to resolve the Condition, and provide specific Resolution Notes.
      </Typography>

      <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} size="small" sx={{ mb: 2, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <ToggleButton key={f.key} value={f.key}>
            {f.label} ({counts[f.key]})
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        <strong>Needs my review</strong> = Pending Review / Review in Progress‑L1 routed to you · <strong>Resolutions to approve</strong> = the owner says it is fixed and needs your sign‑off · <strong>Review in Progress‑L2</strong> = approved by you, now with the L2 reviewer · <strong>Approved/Active</strong> = confirmed and prioritized.
      </Typography>

      <InlineNotice notice={notice} onClose={() => setNotice(null)} />

      <Stack spacing={2}>
        {loading && filtered.length === 0 && <Alert severity="info">Loading OE Conditions…</Alert>}
        {!loading && filtered.length === 0 && <Alert severity="success">Nothing here — all caught up.</Alert>}
        {filtered.map((t) => {
          const f = formOf(t);
          const mine = isL1For(t, viewer, viewer.routing);
          const isOpen = openId === t.id;
          const busy = busyId === t.id;
          const canAct = mine && ['submitted', 'review_l1'].includes(t.status);
          const canAmend = mine && t.status === CONDITION_STATUS.under_review;
          const resolutionLevel = canApproveResolution(t, viewer, viewer.routing);
          const revisable = canRevise(t, viewer, viewer.routing);
          return (
            <Card key={t.id} variant="outlined" sx={{ borderColor: isOpen ? 'primary.main' : 'divider' }}>
              <CardContent sx={{ pb: isOpen ? 2 : 1.5, '&:last-child': { pb: isOpen ? 2 : 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <ConditionHeader condition={t} org={viewer.org} />
                  </Box>
                  <Tooltip title={isOpen ? 'Collapse' : canAct ? 'Open for review' : 'Details'}>
                    <IconButton size="small" onClick={() => toggleOpen(t)} aria-expanded={isOpen} aria-label="Open">
                      <ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <SeverityChips condition={t} />
                </Box>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Typography variant="body2" sx={{ mt: 2, mb: 2, whiteSpace: 'pre-wrap' }}>
                    {t.description}
                  </Typography>
                  {t.l1?.returnNote && t.status === CONDITION_STATUS.review_l1 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Returned by L2: {t.l1.returnNote}
                    </Alert>
                  )}
                  <Divider sx={{ mb: 2 }} />

                  {(canAct || canAmend) && (
                    <Box>
                      <Typography variant="overline" color="text.secondary">{canAmend ? 'Your review (editable until L2 confirms)' : 'Your verification'}</Typography>
                      <Stack spacing={1.5} sx={{ mt: 1 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                          <TextField select size="small" label="Verified severity (I–IV)" value={f.sev} onChange={(e) => setForm(t.id, { sev: e.target.value })} sx={{ minWidth: 240, flex: 1 }}>
                            {SEVERITY_LEVELS.map((s) => (
                              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                            ))}
                          </TextField>
                          <TextField select size="small" label="Proposed REF Action" value={f.ref} onChange={(e) => setForm(t.id, { ref: e.target.value })} sx={{ minWidth: 240, flex: 1 }}>
                            <MenuItem value="">
                              <em>None yet</em>
                            </MenuItem>
                            {REF_ACTIONS.map((r) => (
                              <MenuItem key={r.value} value={r.value}>
                                {r.label} — {r.description}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                        <OwnerFields org={viewer.org} members={viewer.members} value={f.owner} onChange={(owner) => setForm(t.id, { owner })} allowPerson={false} />
                        <TextField size="small" fullWidth multiline minRows={2} label="Resolution Notes" value={f.note} onChange={(e) => setForm(t.id, { note: e.target.value })} />
                      </Stack>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5, flexWrap: 'wrap' }}>
                        {canAmend ? (
                          <Button startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <EditIcon />} variant="contained" size="small" disabled={busy} onClick={() => amend(t)}>
                            Save changes
                          </Button>
                        ) : (
                          <>
                            <Button startIcon={<CloseIcon />} color="error" variant="outlined" size="small" disabled={busy} onClick={() => setDialog({ kind: 'reject', condition: t })}>
                              Not Approved
                            </Button>
                            <Button startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />} variant="contained" color="success" size="small" disabled={busy} onClick={() => approve(t)}>
                              {isSingleStep(t, viewer.routing) ? 'Approve (Approved/Active)' : 'Approved for L2/DH Review'}
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  )}

                  {t.status === CONDITION_STATUS.resolution_pending && (
                    <Box>
                      <Typography variant="overline" color="text.secondary">Resolution submitted</Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>{t.resolution?.submittedByName || 'Owner'}:</strong> {t.resolution?.note}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
                        <Chip size="small" label={t.resolution?.l1ApprovedAt ? 'L1 approved' : 'Awaiting L1'} color={t.resolution?.l1ApprovedAt ? 'success' : 'default'} />
                        {!isSingleStep(t, viewer.routing) && <Chip size="small" label={t.resolution?.l2ApprovedAt ? 'L2 approved' : 'Awaiting L2'} color={t.resolution?.l2ApprovedAt ? 'success' : 'default'} />}
                      </Stack>
                      {resolutionLevel === 'l1' && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <Button size="small" variant="outlined" color="warning" startIcon={<ReplayIcon />} disabled={busy} onClick={() => setDialog({ kind: 'return_resolution', condition: t })}>
                            Return to owner
                          </Button>
                          <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} disabled={busy} onClick={() => run(t.id, async () => { const done = await approveResolution(t.id, { level: 'l1', ...actor, singleStep: isSingleStep(t, viewer.routing) }); notifyConditionEvent(done ? 'resolved' : 'resolution_l1_approved', t, viewer, { after: done ? { status: 'resolved' } : {} }); }, 'Resolution approved at L1.')}>
                            Approve resolution
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                  {['approved', 'resolved', 'rejected', 'withdrawn', 'cancelled'].includes(t.status) && (
                    <Box>
                      <Typography variant="overline" color="text.secondary">Outcome</Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                        {(t.refActions || []).map((a, i) => (
                          <Chip key={i} label={`${refActionLabel(a.type).toUpperCase()}${a.notes ? `: ${a.notes}` : ''}`} size="small" variant="outlined" />
                        ))}
                        {t.status === 'resolved' && <Chip icon={<CheckIcon />} label={`Resolved by ${t.resolvedByName || 'owner'}`} size="small" color="success" />}
                        {t.status === 'rejected' && t.rejectionReason && <Typography variant="body2" color="text.secondary">{t.rejectionReason}</Typography>}
                        {t.status === 'cancelled' && <Typography variant="body2" color="text.secondary">Cancelled: {t.cancelReason}</Typography>}
                      </Stack>
                      {t.resolutionNote && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {t.resolutionNote}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2, flexWrap: 'wrap' }}>
                    {revisable && ['rejected', 'under_review'].includes(t.status) && (
                      <Button size="small" variant="text" startIcon={<ReplayIcon />} disabled={busy} onClick={() => setDialog({ kind: 'revise', condition: t })}>
                        Revise my decision
                      </Button>
                    )}
                    {canHide(t) && (
                      <Button size="small" variant="text" color="inherit" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={() => run(t.id, () => hideConditionFor(t.id, viewer.uid), 'Hidden from your lists (the record is kept).')}>
                        Delete from my list
                      </Button>
                    )}
                    {canCancel(viewer) && !['cancelled', 'withdrawn'].includes(t.status) && (
                      <Button size="small" variant="text" color="error" disabled={busy} onClick={() => setDialog({ kind: 'cancel', condition: t })}>
                        Cancel (admin)
                      </Button>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <NoteDialog
        open={dialog?.kind === 'reject'}
        title="Not Approved"
        body="Tell the entrant why this OE Condition is not approved. They see this note on their submission."
        label="Reason"
        confirmLabel="Mark Not Approved"
        confirmColor="error"
        busy={Boolean(busyId)}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => run(dialog.condition.id, async () => { await flsRejectTRL(dialog.condition.id, { reason, rejectedBy: viewer.uid, rejectedByName: viewer.name, actingFor: viewer.actingFor }); notifyConditionEvent('not_approved', dialog.condition, viewer, { note: reason }); }, 'Marked Not Approved.')}
      />
      <NoteDialog
        open={dialog?.kind === 'return_resolution'}
        title="Return the resolution to the owner"
        body="The condition goes back to Approved/Active with your note; the owner resubmits when done."
        label="What still needs to happen?"
        confirmLabel="Return"
        confirmColor="warning"
        busy={Boolean(busyId)}
        onClose={() => setDialog(null)}
        onConfirm={(note) => run(dialog.condition.id, async () => { await returnResolution(dialog.condition.id, { note, ...actor }); notifyConditionEvent('resolution_returned', dialog.condition, viewer, { note }); }, 'Resolution returned to the owner.')}
      />
      <NoteDialog
        open={dialog?.kind === 'revise'}
        title="Revise your decision"
        body="This reopens the item at your review step so you can decide again. The entrant and the L2 reviewer are told."
        label="Why?"
        confirmLabel="Reopen at L1"
        busy={Boolean(busyId)}
        onClose={() => setDialog(null)}
        onConfirm={(note) => run(dialog.condition.id, async () => { await reviseDecision(dialog.condition.id, { toStatus: 'review_l1', note, ...actor }); notifyConditionEvent('revised', dialog.condition, viewer, { note }); }, 'Reopened at L1.')}
      />
      <NoteDialog
        open={dialog?.kind === 'cancel'}
        title="Cancel this OE Condition (administrator)"
        body="The condition disappears from every queue and dashboard but stays in reports as Cancelled, with your reason and name."
        label="Reason"
        confirmLabel="Cancel condition"
        confirmColor="error"
        busy={Boolean(busyId)}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => run(dialog.condition.id, async () => { await cancelCondition(dialog.condition.id, { reason, ...actor }); notifyConditionEvent('cancelled', dialog.condition, viewer, { note: reason }); }, 'Condition cancelled.')}
      />
    </Box>
  );
}

export default ReviewQueue;
